import React, { useState, useEffect } from 'react';

const PowerMilkPreorder = () => {
  // State management
  const [step, setStep] = useState(0);
  const [availableOrders, setAvailableOrders] = useState(732);
  const [fadeIn, setFadeIn] = useState(true);
  const [answers, setAnswers] = useState({
    goal: '',
    activityLevel: '',
    weight: '',
    trainingFrequency: '',
    dietPreferences: ''
  });
  const [recommendation, setRecommendation] = useState({
    bottlesPerWeek: 0,
    price: 0
  });
  const [discount, setDiscount] = useState(0);
  const [referrals, setReferrals] = useState(0);
  const [monthsAdvanced, setMonthsAdvanced] = useState(0);
  const [contactInfo, setContactInfo] = useState({
    email: '',
    name: ''
  });
  const [isCompleted, setIsCompleted] = useState(false);

  // Calculate recommendation based on user inputs
  useEffect(() => {
    if (step === 6) {
      let bottles = 2; // Default
      
      // Calculate bottles based on answers
      if (answers.goal === 'spieropbouw') {
        bottles += 1;
      }
      
      if (answers.activityLevel === 'hoog') {
        bottles += 1;
      }
      
      if (parseInt(answers.weight) > 80) {
        bottles += 1;
      }
      
      if (answers.trainingFrequency === '4-5 keer') {
        bottles += 1;
      } else if (answers.trainingFrequency === '6+ keer') {
        bottles += 2;
      }
      
      setRecommendation({
        bottlesPerWeek: bottles,
        price: bottles * 6.95
      });
    }
  }, [step, answers]);

  // Update discount based on referrals and months paid in advance
  useEffect(() => {
    setDiscount(referrals + monthsAdvanced);
  }, [referrals, monthsAdvanced]);

  // Handle transitions between steps
  const nextStep = () => {
    setFadeIn(false);
    setTimeout(() => {
      setStep(prevStep => prevStep + 1);
      setFadeIn(true);
    }, 300);
  };

  const previousStep = () => {
    setFadeIn(false);
    setTimeout(() => {
      setStep(prevStep => prevStep - 1);
      setFadeIn(true);
    }, 300);
  };

  // Handle answer selection
  const selectAnswer = (question, answer) => {
    setAnswers(prev => ({
      ...prev,
      [question]: answer
    }));
    nextStep();
  };

  // Handle contact info updates
  const updateContactInfo = (field, value) => {
    setContactInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle referral count updates
  const updateReferrals = (change) => {
    setReferrals(prev => {
      const newValue = prev + change;
      return Math.min(Math.max(newValue, 0), 10);
    });
  };

  // Handle advance payment months updates
  const updateMonthsAdvanced = (change) => {
    setMonthsAdvanced(prev => {
      const newValue = prev + change;
      return Math.min(Math.max(newValue, 0), 6);
    });
  };

  // Complete the pre-order process
  const completeOrder = () => {
    // In a real implementation, this would submit the order
    setIsCompleted(true);
    nextStep();
  };

  // Progress indicator
  const ProgressBar = () => {
    const totalSteps = 9;
    const progress = Math.min((step / totalSteps) * 100, 100);
    
    return (
      <div className="w-full h-1 bg-gray-800 fixed top-0 left-0">
        <div 
          className="h-full bg-white transition-all duration-500 ease-in-out"
          style={{ width: `${progress}%` }}
        />
      </div>
    );
  };

  return (
    <div className="bg-black text-white min-h-screen flex flex-col">
      {step > 0 && step < 9 && <ProgressBar />}
      
      <div 
        className={`flex-1 flex flex-col items-center justify-center p-6 transition-opacity duration-300 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}
      >
        {/* Step 0: Initial Brand Presentation */}
        {step === 0 && (
          <div className="flex flex-col items-center text-center max-w-lg">
            <div className="mb-8">
              <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M60 10C32.3858 10 10 32.3858 10 60C10 87.6142 32.3858 110 60 110C87.6142 110 110 87.6142 110 60C110 32.3858 87.6142 10 60 10Z" stroke="white" strokeWidth="2"/>
                <path d="M45 40V80M60 40V80M75 40V80" stroke="white" strokeWidth="2"/>
                <path d="M35 50H85M35 70H85" stroke="white" strokeWidth="2"/>
              </svg>
            </div>
            <h1 className="text-4xl font-bold mb-4">PowerMilk</h1>
            <p className="text-xl mb-8">Premium proteïne. Persoonlijk geleverd.</p>
            <div className="text-gray-400 mb-12">Nog {availableOrders} orders beschikbaar</div>
            <button 
              onClick={nextStep} 
              className="bg-white text-black px-8 py-3 rounded-full font-medium hover:bg-gray-200 transition-colors duration-300"
            >
              Reserveer Nu
            </button>
          </div>
        )}

        {/* Step 1: Personal Goal */}
        {step === 1 && (
          <div className="flex flex-col items-center text-center max-w-xl">
            <h2 className="text-3xl font-bold mb-12">Wat is je primaire doel?</h2>
            <div className="grid grid-cols-1 gap-4 w-full">
              {['Spieropbouw', 'Herstel', 'Performance', 'Algemene gezondheid'].map((goal) => (
                <button
                  key={goal}
                  onClick={() => selectAnswer('goal', goal.toLowerCase())}
                  className="bg-gray-900 hover:bg-gray-800 py-4 px-6 rounded-lg text-left border border-gray-800 transition-colors duration-200"
                >
                  {goal}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Activity Level */}
        {step === 2 && (
          <div className="flex flex-col items-center text-center max-w-xl">
            <h2 className="text-3xl font-bold mb-12">Hoe zou je je activiteitsniveau omschrijven?</h2>
            <div className="grid grid-cols-1 gap-4 w-full">
              {['Laag', 'Gemiddeld', 'Hoog', 'Zeer hoog'].map((level) => (
                <button
                  key={level}
                  onClick={() => selectAnswer('activityLevel', level.toLowerCase())}
                  className="bg-gray-900 hover:bg-gray-800 py-4 px-6 rounded-lg text-left border border-gray-800 transition-colors duration-200"
                >
                  {level}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Body Weight */}
        {step === 3 && (
          <div className="flex flex-col items-center text-center max-w-xl">
            <h2 className="text-3xl font-bold mb-12">Wat is je lichaamsgewicht in kg?</h2>
            <div className="grid grid-cols-2 gap-4 w-full">
              {['50-60', '60-70', '70-80', '80-90', '90-100', '100+'].map((weight) => (
                <button
                  key={weight}
                  onClick={() => selectAnswer('weight', weight)}
                  className="bg-gray-900 hover:bg-gray-800 py-4 px-6 rounded-lg text-center border border-gray-800 transition-colors duration-200"
                >
                  {weight} kg
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Training Frequency */}
        {step === 4 && (
          <div className="flex flex-col items-center text-center max-w-xl">
            <h2 className="text-3xl font-bold mb-12">Hoe vaak train je per week?</h2>
            <div className="grid grid-cols-1 gap-4 w-full">
              {['0-1 keer', '2-3 keer', '4-5 keer', '6+ keer'].map((frequency) => (
                <button
                  key={frequency}
                  onClick={() => selectAnswer('trainingFrequency', frequency)}
                  className="bg-gray-900 hover:bg-gray-800 py-4 px-6 rounded-lg text-left border border-gray-800 transition-colors duration-200"
                >
                  {frequency}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 5: Diet Preferences */}
        {step === 5 && (
          <div className="flex flex-col items-center text-center max-w-xl">
            <h2 className="text-3xl font-bold mb-12">Heb je specifieke dieetvoorkeuren?</h2>
            <div className="grid grid-cols-1 gap-4 w-full">
              {['Geen specifieke voorkeuren', 'Vegetarisch', 'Lactosevrij', 'Geen toegevoegde suikers'].map((preference) => (
                <button
                  key={preference}
                  onClick={() => selectAnswer('dietPreferences', preference)}
                  className="bg-gray-900 hover:bg-gray-800 py-4 px-6 rounded-lg text-left border border-gray-800 transition-colors duration-200"
                >
                  {preference}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 6: Subscription Proposal */}
        {step === 6 && (
          <div className="flex flex-col items-center text-center max-w-xl">
            <h2 className="text-3xl font-bold mb-8">Jouw PowerMilk Advies</h2>
            <div className="mb-8">
              <svg width="180" height="280" viewBox="0 0 180 280" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="40" y="40" width="100" height="200" rx="10" stroke="white" strokeWidth="2"/>
                <rect x="50" y="60" width="80" height="160" rx="5" stroke="white" strokeWidth="2"/>
                <path d="M65 30L65 10" stroke="white" strokeWidth="2"/>
                <path d="M115 30L115 10" stroke="white" strokeWidth="2"/>
                <path d="M65 10H115" stroke="white" strokeWidth="2"/>
                <text x="90" y="140" fill="white" fontSize="24" fontWeight="bold" textAnchor="middle">50g</text>
              </svg>
            </div>
            <p className="text-xl mb-6">Gebaseerd op je profiel, adviseren we:</p>
            <div className="bg-gray-900 p-6 rounded-lg mb-8 w-full">
              <p className="text-2xl font-bold mb-2">{recommendation.bottlesPerWeek} flessen per week</p>
              <p className="text-gray-400">€{recommendation.price.toFixed(2)} per week</p>
            </div>
            <p className="text-gray-400 mb-8">Perfect voor jouw {answers.goal} doelen en {answers.trainingFrequency.toLowerCase()} trainingsfrequentie.</p>
            <div className="flex space-x-4">
              <button 
                onClick={previousStep} 
                className="border border-white px-6 py-3 rounded-full font-medium hover:bg-gray-900 transition-colors duration-300"
              >
                Aanpassen
              </button>
              <button 
                onClick={nextStep} 
                className="bg-white text-black px-8 py-3 rounded-full font-medium hover:bg-gray-200 transition-colors duration-300"
              >
                Bevestigen
              </button>
            </div>
          </div>
        )}

        {/* Step 7: Referrals & Discounts */}
        {step === 7 && (
          <div className="flex flex-col items-center text-center max-w-xl">
            <h2 className="text-3xl font-bold mb-8">Exclusieve Kortingen</h2>
            <div className="bg-gray-900 p-6 rounded-lg mb-8 w-full">
              <div className="mb-6">
                <p className="text-xl mb-4">Verwijs vrienden en ontvang €1 korting per referral</p>
                <div className="flex items-center justify-center">
                  <button 
                    onClick={() => updateReferrals(-1)} 
                    className="w-10 h-10 rounded-full border border-gray-700 flex items-center justify-center hover:bg-gray-800"
                    disabled={referrals === 0}
                  >
                    -
                  </button>
                  <span className="mx-6 text-xl">{referrals} vrienden</span>
                  <button 
                    onClick={() => updateReferrals(1)} 
                    className="w-10 h-10 rounded-full border border-gray-700 flex items-center justify-center hover:bg-gray-800"
                    disabled={referrals === 10}
                  >
                    +
                  </button>
                </div>
                <p className="text-gray-500 text-sm mt-2">Max. 10 referrals</p>
              </div>
              
              <div className="mb-6">
                <p className="text-xl mb-4">Vooruit betalen voor extra korting</p>
                <div className="flex items-center justify-center">
                  <button 
                    onClick={() => updateMonthsAdvanced(-1)} 
                    className="w-10 h-10 rounded-full border border-gray-700 flex items-center justify-center hover:bg-gray-800"
                    disabled={monthsAdvanced === 0}
                  >
                    -
                  </button>
                  <span className="mx-6 text-xl">{monthsAdvanced} maanden</span>
                  <button 
                    onClick={() => updateMonthsAdvanced(1)} 
                    className="w-10 h-10 rounded-full border border-gray-700 flex items-center justify-center hover:bg-gray-800"
                    disabled={monthsAdvanced === 6}
                  >
                    +
                  </button>
                </div>
                <p className="text-gray-500 text-sm mt-2">Max. 6 maanden</p>
              </div>
              
              <div className="pt-4 border-t border-gray-800">
                <p className="text-lg">Totale korting: €{discount.toFixed(2)} per week</p>
                <p className="text-xl font-bold mt-2">
                  Nieuwe prijs: €{Math.max(0, recommendation.price - discount).toFixed(2)} per week
                </p>
              </div>
            </div>
            
            <div className="flex space-x-4">
              <button 
                onClick={previousStep} 
                className="border border-white px-6 py-3 rounded-full font-medium hover:bg-gray-900 transition-colors duration-300"
              >
                Terug
              </button>
              <button 
                onClick={nextStep} 
                className="bg-white text-black px-8 py-3 rounded-full font-medium hover:bg-gray-200 transition-colors duration-300"
              >
                Doorgaan
              </button>
            </div>
          </div>
        )}

        {/* Step 8: Contact Information */}
        {step === 8 && (
          <div className="flex flex-col items-center text-center max-w-xl">
            <h2 className="text-3xl font-bold mb-8">Bijna klaar</h2>
            <p className="text-xl mb-8">Vul je gegevens in om je pre-order te bevestigen</p>
            
            <div className="w-full mb-8">
              <div className="mb-4">
                <label htmlFor="name" className="block text-left text-sm font-medium mb-2">Naam</label>
                <input
                  type="text"
                  id="name"
                  value={contactInfo.name}
                  onChange={(e) => updateContactInfo('name', e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-white"
                  placeholder="Jouw volledige naam"
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="email" className="block text-left text-sm font-medium mb-2">E-mail</label>
                <input
                  type="email"
                  id="email"
                  value={contactInfo.email}
                  onChange={(e) => updateContactInfo('email', e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-white"
                  placeholder="jouw@email.nl"
                />
              </div>
            </div>
            
            <div className="bg-gray-900 p-4 rounded-lg mb-8 w-full">
              <p className="text-sm text-gray-400">
                Door te bevestigen ga je akkoord met onze algemene voorwaarden en privacybeleid. Je kunt je abonnement altijd pauzeren of annuleren.
              </p>
            </div>
            
            <div className="flex space-x-4">
              <button 
                onClick={previousStep} 
                className="border border-white px-6 py-3 rounded-full font-medium hover:bg-gray-900 transition-colors duration-300"
              >
                Terug
              </button>
              <button 
                onClick={completeOrder} 
                className="bg-white text-black px-8 py-3 rounded-full font-medium hover:bg-gray-200 transition-colors duration-300"
                disabled={!contactInfo.email || !contactInfo.name}
              >
                Bevestig Pre-order
              </button>
            </div>
          </div>
        )}

        {/* Step 9: Confirmation */}
        {step === 9 && (
          <div className="flex flex-col items-center text-center max-w-xl">
            <div className="mb-8">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="40" cy="40" r="38" stroke="white" strokeWidth="2"/>
                <path d="M25 40L35 50L55 30" stroke="white" strokeWidth="2"/>
              </svg>
            </div>
            <h2 className="text-3xl font-bold mb-4">Bedankt voor je pre-order!</h2>
            <p className="text-xl mb-8">
              We hebben je bestelling ontvangen en zullen je binnenkort contacteren met meer informatie.
            </p>
            <div className="bg-gray-900 p-6 rounded-lg mb-8 w-full">
              <h3 className="text-xl font-bold mb-4">Je Bestelling</h3>
              <p className="mb-2">{recommendation.bottlesPerWeek} flessen per week</p>
              <p className="mb-4">€{Math.max(0, recommendation.price - discount).toFixed(2)} per week</p>
              <div className="pt-4 border-t border-gray-800">
                <p className="text-gray-400">Je ontvangt binnen 24 uur een bevestigingsmail op {contactInfo.email}</p>
              </div>
            </div>
            <p className="text-gray-400 mb-8">
              De eerste leveringen starten in mei 2025. We houden je op de hoogte van alle updates.
            </p>
            {referrals > 0 && (
              <div className="bg-gray-900 p-4 rounded-lg mb-8 w-full">
                <p className="text-white">
                  Vergeet niet om je vrienden uit te nodigen voor je extra korting!
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Footer: Only show on first and last steps */}
      {(step === 0 || step === 9) && (
        <footer className="py-6 px-8 text-gray-500 text-sm">
          <div className="flex justify-between">
            <div>© 2025 PowerMilk</div>
            <div className="flex space-x-4">
              <a href="#" className="hover:text-white transition-colors duration-200">Voorwaarden</a>
              <a href="#" className="hover:text-white transition-colors duration-200">Privacy</a>
              <a href="#" className="hover:text-white transition-colors duration-200">Contact</a>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default PowerMilkPreorder;